export const ReasoningEffortEnumValues = ["low", "medium", "high"] as const;
export type ReasoningEffortEnumType =
  (typeof ReasoningEffortEnumValues)[number];

// Chat type enum
export const ChatTypeEnumValues = ["COMPLETION", "REALTIME"] as const;
export type ChatTypeEnumType = (typeof ChatTypeEnumValues)[number];

// messageStatusEnum
export const MessageStatusEnumValues = [
  "PENDING",
  "SUCCESS",
  "ERROR",
  "CANCELLED",
] as const;
export type MessageStatusEnumType = (typeof MessageStatusEnumValues)[number];

// Realtime conversation item role enum
export const RealtimeConversationItemRoleEnumValues = [
  "user",
  "assistant",
  "system",
] as const;

// messageRoleEnum. Lower-case, because we match the OpenAI message types.
export const MessageRoleEnumValues = [
  "system",
  "user",
  "assistant",
  "tool",
] as const;
export type MessageRoleEnumType = (typeof MessageRoleEnumValues)[number];

// messageFileOriginStatusEnum
export const MessageFileOriginEnumValues = [
  "USER_INPUT",
  "PLUGIN_OUTPUT",
  "CODE_RESULT",
] as const;
export type MessageFileOriginEnumType =
  (typeof MessageFileOriginEnumValues)[number];

// feedbackValueEnum
export const FeedbackValueEnumValues = ["LIKE", "DISLIKE"] as const;
export type FeedbackValueEnumType = (typeof FeedbackValueEnumValues)[number];

// workspaceUserRoleEnum
export const RoleEnumValues = ["ADMIN", "USER"] as const;
export type RoleEnumTypes = (typeof RoleEnumValues)[number];

// workspaceTypeEnum
// Workspace types - split into public and private. This is to allow validator to prevent use of PERSONAL
export const WorkspaceTypeValuesPrivate = ["PERSONAL"] as const;
export const WorkspaceTypeValuesPublic = ["CONTEXTED", "BASIC_SHARED"] as const;
export const WorkspaceTypeValuesAll = [
  ...WorkspaceTypeValuesPublic,
  ...WorkspaceTypeValuesPrivate,
] as const;
export type WorkspaceTypeEnumType = (typeof WorkspaceTypeValuesAll)[number];

// pluginTypeEnum
export const PluginTypeEnumValues = [
  "CLINICAL_TRIALS_GOV",
  "PUBMED",
  "WEBREADER",
  "MS_OFFICE_WRITER",
  "CODE_INTERPRETER",
  "DALL_E",
  "SHAREPOINT",
  "SERVICE_NOW_KNOWLEDGE_ARTICLES",
  "SF_SALES_INTELLIGENCE",
  "RESEARCH_AI",
  "MEDDRA",
  "ISEARCH",
  "HAS_DATABASE",
] as const;
export type PluginTypeEnumTypes = (typeof PluginTypeEnumValues)[number];

// workspaceTypeEnum
export const WorkspaceTypeValues = ["CHAT", "CHAT_HISTORY"] as const;
export type WorkspaceTypeEnumTypes = (typeof WorkspaceTypeValues)[number];

// Chat workspace types
export const ChatWorkspaceTypeValues = ["CHAT", "CHAT_HISTORY"] as const;
export type ChatWorkspaceTypeEnumTypes =
  (typeof ChatWorkspaceTypeValues)[number];

/**
 * AiProfilePopulatedStatusEnum
 *
 * This enum represents the various statuses of an AI profile.
 *
 * - `DRAFT`: The AI profile is in draft status and not yet published. Will be displayed for admins only in profiles list in ChatAI app.
 * - `PUBLISHED`: The AI profile is published and available for use. Will be displayed for all users.
 * - `RETIRED`: The AI profile is retired and no longer in active use. Will be displayed only for admins in the ChatAI section in admin panel.
 */
export const AiProfilePopulatedStatusEnumValues = [
  "DRAFT",
  "PUBLISHED",
  "RETIRED",
] as const;
export type AiProfilePopulatedStatusEnumType =
  (typeof AiProfilePopulatedStatusEnumValues)[number];

/** "pending" = computation in progress; "completed" = highlights are ready to display. */
export const CitationHighlightCacheStatusEnumValues = [
  "pending",
  "completed",
] as const;
export type CitationHighlightCacheStatusEnumType =
  (typeof CitationHighlightCacheStatusEnumValues)[number];

export const AiProfileShareTypeEnumValues = [
  "USERS",
  "WORKSPACE",
  "GALLERY",
] as const;

/**
 * Aggregate lifecycle for the profile-scoped knowledge graph. `COMPLETE` is
 * the only state that profile chat/realtime/graph retrieval treats as ready.
 */
export const AiProfileGraphStatusEnumValues = [
  "EMPTY",
  "QUEUED",
  "INDEXING",
  "EXTRACTING",
  "COMMUNITY_DETECTION",
  "COMPLETE",
  "FAILED",
] as const;
export type AiProfileGraphStatusEnumType =
  (typeof AiProfileGraphStatusEnumValues)[number];

/**
 * Per-profile-file lifecycle within one graph generation. `SKIPPED` means the
 * file was processable but produced no graphable chunks, so it counts as
 * complete for aggregate readiness.
 */
export const AiProfileGraphFileStatusEnumValues = [
  "QUEUED",
  "INDEXING",
  "EXTRACTING",
  "COMPLETE",
  "FAILED",
  "SKIPPED",
] as const;
export type AiProfileGraphFileStatusEnumType =
  (typeof AiProfileGraphFileStatusEnumValues)[number];

/** Non-terminal profile-file graph states that still represent active work. */
export const ActiveAiProfileGraphFileStatusEnumValues = [
  "QUEUED",
  "INDEXING",
  "EXTRACTING",
] as const satisfies readonly AiProfileGraphFileStatusEnumType[];
export type ActiveAiProfileGraphFileStatusEnumType =
  (typeof ActiveAiProfileGraphFileStatusEnumValues)[number];
